import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';

import 'chartjs-plugin-datalabels/dist/chartjs-plugin-datalabels.js';

import { ColorPickerModule } from 'ngx-color-picker';
import { ClipboardModule } from 'ngx-clipboard';
import { UiSwitchModule } from 'ngx-ui-switch';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { TranslateModule } from '@ngx-translate/core';

import { CrazyPopupRootComponent } from './lead-generation.component';
import { AppConfigService } from './shared/services/app-config.service';
import { ScriptLoaderService } from './shared/services/script-loader.service';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { ChartsModule } from 'ng2-charts/ng2-charts';
import { StatsService } from './stats/stats.service';
import { HorizontalBarChartComponent } from './shared/charts/horizontal-bar-chart/horizontal-bar-chart.component';
import { RouterModule } from '@angular/router';
import { leadGenerationRoutes } from './lead-generation.routes';
import { SettingsComponent } from './settings/settings.component';
import { BoxComponent } from '../shared/layout/box/box.component';
import { BoxTypeComponent } from './creator/bubble-creator/box-type/box-type.component';
import { BubbleCreatorComponent } from './creator/bubble-creator/bubble-creator.component';
import { ThemeColorComponent } from './creator/bubble-creator/theme-color/theme-color.component';
import { FontBoxComponent } from './creator/bubble-creator/font-box/font-box.component';
import { TextBoxComponent } from './creator/bubble-creator/text-box/text-box.component';
import { LivePreviewBubbleComponent } from './creator/bubble-creator/live-preview/live-preview-bubble.component';
import { LabelInputComponent } from './creator/bubble-creator/text-box/label-input/label-input.component';
import { ColorBubbleComponent } from './creator/bubble-creator/theme-color/color-bubble-set/color-bubble/color-bubble.component';
import { CrazyCallConfigService } from './shared/services/crazycall-config.service';
import { ColorBubbleSetComponent } from './creator/bubble-creator/theme-color/color-bubble-set/color-bubble-set.component';
import { LeadGenerationConfigurationService } from './shared/services/lead-generation-configuration.service';
import { CrazyCallAppIdResolver } from './shared/resolvers/crazycall-appid.resolver';
import { CrazyCallAccountResolver } from './shared/resolvers/crazycall-account.resolver';
import { GlobalSettingsResolve } from './global-settings/global-settings.resolve';
import { ReportsResolve } from './reports/reports.resolve';
import { LayoutModule } from '../shared/layout/layout.module';
import { DisplayConditionListComponent } from './settings/display-condition/display-condition-list.component';
import { ItemComponent } from './settings/display-condition/item/item.component';
import { SharedModule } from '../shared/shared.module';
import { ColorInputComponent } from './creator/bubble-creator/color-input/color-input.component';
import { FontSelectComponent } from './creator/bubble-creator/font-select/font-select.component';
import { CreatorSelectComponent } from './creator-select/creator-select.component';
import { PopupCreatorComponent } from './creator/popup-creator/popup-creator.component';
import {
  MatAutocompleteModule,
  MatButtonModule,
  MatCheckboxModule,
  MatDividerModule,
  MatIconModule,
  MatInputModule,
  MatRadioModule,
  MatSelectModule,
  MatSortModule,
  MatTableModule
} from '@angular/material';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { ItemInputComponent } from './settings/display-condition/item/item-input/item-input.component';
import { ItemSelectComponent } from './settings/display-condition/item/item-select/item-select.component';
import { ItemTimeComponent } from './settings/display-condition/item/item-time/item-time.component';
import { ItemMainSelectComponent } from './settings/display-condition/item/item-main-select/item-main-select.component';
import { DynamicModule } from 'ng-dynamic-component';
import { dynamicComponents } from './shared/domain/display.condition.type';
import { ItemService } from './settings/display-condition/item/item.service';
import { LivePreviewPopupComponent } from './creator/popup-creator/live-preview/live-preview-popup.component';
import { LivePreviewService } from './shared/services/live-preview.service';
import { PopupThemeColorComponent } from './creator/popup-creator/theme-color/popup-theme-color.component';
import { PopupTextBoxComponent } from './creator/popup-creator/text-box/popup-text-box.component';
import { PopupFontBoxComponent } from './creator/popup-creator/font-box/popup-font-box.component';
import { AceEditorModule } from 'ng2-ace-editor';
import { CodeBoxComponent } from './shared/code-box/code-box.component';
import { LeadGenerationStylesService } from './shared/services/lead-generation-styles.service';
import { LeftMenuComponent } from './left-menu/left-menu.component';
import { ProjectsTableDataSource } from './projects/projects-table/projects-table.data';
import { ProjectsTableComponent } from './projects/projects-table/projects-table.component';
import { ProjectsService } from './projects/projects.service';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { ProjectsResolve } from './projects/projects.resolve';
import { ProjectResolve } from './projects/project.resolve';
import { projectsReducer } from './projects/projects.reducer';
import { globalSettingsReducer } from './global-settings/global-settings.reducer';
import { reportsReducer } from './reports/reports.reducer';
import { ProjectsEffects } from './projects/projects.effects';
import { GlobalSettingsEffects } from './global-settings/global-settings.effects';
import { InstallationComponent } from './installation/installation.component';
import { CloseTypeComponent } from './creator/bubble-creator/close-type/close-type.component';
import { SimpleCodeBoxComponent } from './shared/simple-code-box/simple-code-box.component';
import { CountryCodeComponent } from './shared/country-code/country-code.component';
import { PhoneNumbersDropdownComponent } from './shared/phone-numbers-dropdown/phone-numbers-dropdown.component';
import { ItemPagesComponent } from './settings/display-condition/item/item-pages/item-pages.component';
import { GlobalSettingsComponent } from './global-settings/global-settings.component';
import { ReportsComponent } from './reports/reports.component';
import { ReportFiltersComponent } from './reports/report-filters/report-filters.component';
import { ReportStatsComponent } from './reports/report-stats/report-stats.component';
import { ReportsEffects } from './reports/reports.effects';
import { ActivityOverviewChartComponent } from './stats/activity-overview-chart/activity-overview-chart.component';
import { TimeToFirstContactChartComponent } from './stats/time-to-first-contact-chart/time-to-first-contact-chart.component';
import { VerticalBarChartComponent } from './shared/charts/vertical-bar-chart/vertical-bar-chart.component';
import { ChartComponent } from './shared/charts/chart/chart.component';
import { localStorageSyncReducer } from './localStorageSyncReducer';
import { ItemPercentComponent } from './settings/display-condition/item/item-percent/item-percent.component';
import { UI_SWITCH_CONFIGURATION } from '../shared/utils/layoutUtils';
import { IpExclusionComponent } from './global-settings/ip-exclusion/ip-exclusion.component';
import { PositionBoxComponent } from './creator/bubble-creator/position-box/position-box.component';
import { LabelErrorWrapperComponent } from './shared/label-error-wrapper/label-error-wrapper.component';
import { NgxPermissionsModule } from 'ngx-permissions';

@NgModule({
  declarations: [
    CrazyPopupRootComponent,
    HorizontalBarChartComponent,
    BoxTypeComponent,
    TextBoxComponent,
    ThemeColorComponent,
    BubbleCreatorComponent,
    FontBoxComponent,
    SettingsComponent,
    LivePreviewBubbleComponent,
    LabelInputComponent,
    ColorBubbleComponent,
    ColorBubbleSetComponent,
    DisplayConditionListComponent,
    ItemComponent,
    ColorInputComponent,
    FontSelectComponent,
    CreatorSelectComponent,
    PopupCreatorComponent,
    ItemInputComponent,
    ItemSelectComponent,
    ItemTimeComponent,
    ItemMainSelectComponent,
    LivePreviewPopupComponent,
    PopupThemeColorComponent,
    PopupTextBoxComponent,
    PopupFontBoxComponent,
    CodeBoxComponent,
    LeftMenuComponent,
    ProjectsTableComponent,
    InstallationComponent,
    CloseTypeComponent,
    SimpleCodeBoxComponent,
    CountryCodeComponent,
    PhoneNumbersDropdownComponent,
    ItemPagesComponent,
    GlobalSettingsComponent,
    ReportsComponent,
    ReportFiltersComponent,
    ReportStatsComponent,
    ActivityOverviewChartComponent,
    TimeToFirstContactChartComponent,
    VerticalBarChartComponent,
    ChartComponent,
    ItemPercentComponent,
    IpExclusionComponent,
    PositionBoxComponent,
    LabelErrorWrapperComponent
  ],
  imports: [
    BrowserModule,
    HttpClientModule,
    ColorPickerModule,
    ReactiveFormsModule,
    TranslateModule,
    MatInputModule,
    ClipboardModule,
    FormsModule,
    ChartsModule,
    NgxMatSelectSearchModule,
    UiSwitchModule.forRoot(UI_SWITCH_CONFIGURATION),
    BrowserAnimationsModule,
    ToastrModule.forRoot(),
    RouterModule.forChild(leadGenerationRoutes),
    LayoutModule,
    SharedModule,
    MatButtonModule,
    MatRadioModule,
    MatSelectModule,
    MatDividerModule,
    MatCheckboxModule,
    DynamicModule.withComponents(Object.values(dynamicComponents)),
    MatIconModule,
    AceEditorModule,
    MatTableModule,
    MatAutocompleteModule,
    MatSortModule,
    StoreModule.forFeature(
      'leadGeneration',
      {
        projects: projectsReducer,
        globalSettings: globalSettingsReducer,
        reports: reportsReducer
      },
      { metaReducers: [localStorageSyncReducer] }
    ),
    EffectsModule.forFeature([ProjectsEffects, GlobalSettingsEffects, ReportsEffects]),
    NgxPermissionsModule
  ],
  providers: [
    AppConfigService,
    ScriptLoaderService,
    StatsService,
    LeadGenerationConfigurationService,
    CrazyCallAppIdResolver,
    CrazyCallAccountResolver,
    GlobalSettingsResolve,
    ReportsResolve,
    ItemService,
    LivePreviewService,
    CrazyCallConfigService,
    LeadGenerationStylesService,
    ProjectsTableDataSource,
    ProjectsService,
    ProjectsResolve,
    ProjectResolve
  ],
  exports: [
    CrazyPopupRootComponent,
    BubbleCreatorComponent,
    ThemeColorComponent,
    TextBoxComponent,
    LivePreviewBubbleComponent,
    SimpleCodeBoxComponent
  ],
  entryComponents: [
    ItemInputComponent,
    ItemSelectComponent,
    ItemTimeComponent,
    ItemPagesComponent,
    ItemPercentComponent,
    BoxComponent
  ]
})
export class CrazyPopupGeneratorModule {}
